"use client"

import { useEffect } from "react"
import { FormikProvider, useFormik } from "formik"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { FormInput, FormSelect } from "@/components/form-inputs"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { ImageUpload } from "@/components/ui/image-upload"
import { Switch } from "@/components/ui/switch"
import { Loader2 } from "lucide-react"
import { useUpdateTenant } from "@/hooks/use-update-tenant"
import type { AIBillingMode } from "@/lib/ai/langchain/types"

const BILLING_MODE_OPTIONS = [
  { value: "tenant", label: "Tenant wallet" },
  { value: "platform", label: "Platform" },
] as const satisfies readonly { value: AIBillingMode; label: string }[]

interface TenantEditDialogProps {
  tenant: {
    id: string
    name: string
    logo_url: string | null
    disable_free_plan_expiry: boolean
    is_active: boolean
    archived_at: string | null
    conversation_quality_platform_enabled: boolean
    conversation_quality_billing_mode: AIBillingMode
  } | null
  onOpenChange: (open: boolean) => void
  canEditTenantDetails: boolean
  canChangeTenantStatus: boolean
  isPlatformTenant: boolean
  onReactivated: (tenant: { id: string; name: string }) => void
  onUpdated: (updated: {
    id: string
    name?: string
    logo_url?: string | null
    disable_free_plan_expiry?: boolean
    is_active?: boolean
    conversation_quality_platform_enabled?: boolean
    conversation_quality_billing_mode?: AIBillingMode
  }) => void
}

interface TenantEditValues {
  name: string
  disable_free_plan_expiry: boolean
  is_active: boolean
  conversation_quality_platform_enabled: boolean
  conversation_quality_billing_mode: AIBillingMode
}

export function TenantEditDialog({
  tenant,
  onOpenChange,
  canEditTenantDetails,
  canChangeTenantStatus,
  isPlatformTenant,
  onReactivated,
  onUpdated,
}: TenantEditDialogProps) {
  const { isSaving, isUploadingLogo, error, updateTenant, uploadLogo, removeLogo, resetError } =
    useUpdateTenant()
  const formik = useFormik<TenantEditValues>({
    enableReinitialize: true,
    initialValues: {
      name: tenant?.name ?? "",
      disable_free_plan_expiry: tenant?.disable_free_plan_expiry ?? false,
      is_active: tenant?.is_active ?? false,
      conversation_quality_platform_enabled: tenant?.conversation_quality_platform_enabled ?? true,
      conversation_quality_billing_mode: tenant?.conversation_quality_billing_mode ?? "tenant",
    },
    onSubmit: async (values) => {
      if (!tenant) return
      const nameChanged = values.name.trim() !== tenant.name
      const expirySettingChanged =
        values.disable_free_plan_expiry !== tenant.disable_free_plan_expiry
      const statusChanged = values.is_active !== tenant.is_active
      const canSaveName = canEditTenantDetails && nameChanged
      const canSaveStatus = canChangeTenantStatus && statusChanged
      const qualityPatch = canEditTenantDetails ? qualityChanges(values, tenant) : {}
      const tenantReactivated =
        canSaveStatus && tenant.is_active === false && values.is_active === true
      if (
        !canSaveName &&
        !expirySettingChanged &&
        !canSaveStatus &&
        Object.keys(qualityPatch).length === 0
      ) {
        return
      }

      try {
        const patch = {
          ...(canSaveName ? { name: values.name.trim() } : {}),
          ...(expirySettingChanged
            ? { disable_free_plan_expiry: values.disable_free_plan_expiry }
            : {}),
          ...(canSaveStatus ? { is_active: values.is_active } : {}),
          ...qualityPatch,
        }
        await updateTenant(tenant.id, patch)
        onUpdated({ id: tenant.id, ...patch })
        if (tenantReactivated) {
          onReactivated({ id: tenant.id, name: tenant.name })
        }
      } catch {
        /* error is surfaced via the hook's `error` state */
      }
    },
  })

  useEffect(() => {
    if (tenant) {
      resetError()
    }
  }, [tenant, resetError])

  const nameChanged = tenant ? formik.values.name.trim() !== tenant.name : false
  const expirySettingChanged = tenant
    ? formik.values.disable_free_plan_expiry !== tenant.disable_free_plan_expiry
    : false
  const statusChanged = tenant
    ? formik.values.is_active !== tenant.is_active
    : false
  const qualityChanged = tenant
    ? Object.keys(qualityChanges(formik.values, tenant)).length > 0
    : false
  const hasChanges =
    (canEditTenantDetails && nameChanged) ||
    expirySettingChanged ||
    (canChangeTenantStatus && statusChanged) ||
    (canEditTenantDetails && qualityChanged)

  const handleLogoUpload = async (file: File) => {
    if (!tenant) return
    try {
      const { url } = await uploadLogo(tenant.id, file)
      onUpdated({ id: tenant.id, logo_url: url })
    } catch (err) {
      console.error("Logo upload error:", err)
    }
  }

  const handleLogoRemove = async () => {
    if (!tenant) return
    try {
      await removeLogo(tenant.id)
      onUpdated({ id: tenant.id, logo_url: null })
    } catch (err) {
      console.error("Logo remove error:", err)
    }
  }

  return (
    <Dialog open={!!tenant} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {canEditTenantDetails ? "Edit Tenant" : "Tenant Settings"}
          </DialogTitle>
          <DialogDescription>
            {tenant?.id.slice(0, 8)}<span>...</span>
          </DialogDescription>
        </DialogHeader>

        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className="space-y-4">
              {canEditTenantDetails && (
                <FormInput
                  name="name"
                  label="Name"
                  placeholder="Tenant name"
                />
              )}

              <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="disable-free-plan-expiry">
                    Disable Free Plan Expiry
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Exclude this tenant from plan expiry and hide the days-left notice.
                  </p>
                </div>
                <Switch
                  id="disable-free-plan-expiry"
                  checked={formik.values.disable_free_plan_expiry}
                  onCheckedChange={(checked) =>
                    void formik.setFieldValue("disable_free_plan_expiry", checked)
                  }
                />
              </div>

              {canChangeTenantStatus && (
                <div className="flex items-center justify-between gap-4 rounded-md border p-3">
                  <div className="space-y-0.5">
                    <Label htmlFor="tenant-active">Tenant Active</Label>
                    <p className="text-xs text-muted-foreground">
                      {getTenantStatusDescription(
                        tenant?.archived_at ?? null,
                        isPlatformTenant,
                      )}
                    </p>
                  </div>
                  <Switch
                    id="tenant-active"
                    checked={formik.values.is_active}
                    onCheckedChange={(checked) =>
                      void formik.setFieldValue("is_active", checked)
                    }
                    disabled={
                      !!tenant?.archived_at ||
                      (isPlatformTenant && tenant?.is_active === true)
                    }
                  />
                </div>
              )}

              {canEditTenantDetails && (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label htmlFor="conversation-quality-enabled">Conversation Quality</Label>
                      <p className="text-xs text-muted-foreground">
                        Off, the tenant cannot turn it on, and sessions that end meanwhile are not
                        reviewed later.
                      </p>
                    </div>
                    <Switch
                      id="conversation-quality-enabled"
                      checked={formik.values.conversation_quality_platform_enabled}
                      onCheckedChange={(checked) =>
                        void formik.setFieldValue("conversation_quality_platform_enabled", checked)
                      }
                    />
                  </div>
                  <FormSelect
                    name="conversation_quality_billing_mode"
                    label="Paid by"
                    options={BILLING_MODE_OPTIONS}
                    description="Who pays for its model calls. A change applies to new calls only."
                  />
                </div>
              )}

              {canEditTenantDetails && (
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <ImageUpload
                    currentImageUrl={tenant?.logo_url ?? null}
                    onUpload={handleLogoUpload}
                    onRemove={handleLogoRemove}
                    isUploading={isUploadingLogo}
                    shape="circle"
                  />
                </div>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!hasChanges || isSaving}>
                {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <span>Save</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

/** The quality fields whose value differs from the tenant's, as the PATCH takes them. */
function qualityChanges(
  values: TenantEditValues,
  tenant: NonNullable<TenantEditDialogProps["tenant"]>
): {
  conversation_quality_platform_enabled?: boolean
  conversation_quality_billing_mode?: AIBillingMode
} {
  return {
    ...(values.conversation_quality_platform_enabled !== tenant.conversation_quality_platform_enabled
      ? { conversation_quality_platform_enabled: values.conversation_quality_platform_enabled }
      : {}),
    ...(values.conversation_quality_billing_mode !== tenant.conversation_quality_billing_mode
      ? { conversation_quality_billing_mode: values.conversation_quality_billing_mode }
      : {}),
  }
}

function getTenantStatusDescription(
  archivedAt: string | null,
  platformTenant: boolean,
): string {
  if (archivedAt) {
    return "Restore this tenant before changing its active status."
  }
  if (platformTenant) {
    return "The platform tenant cannot be deactivated."
  }
  return "Inactive tenants cannot use their workspace until reactivated."
}
